import { Reference } from '@apollo/client';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTogglePinCommunityPostMutation } from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';

// Apollo serialises the pinned variant as posts({"type":"pinned"}).
const PINNED_STORE_FIELD = 'pinned';

export const useTogglePinPost = (communityId: CommunityId) => {
  const [togglePinMutation] = useTogglePinCommunityPostMutation();

  return useCallback(
    (postId: PostId, pinned: boolean) =>
      togglePinMutation({
        variables: { communityId, postId, pinned },
        optimisticResponse: {
          __typename: 'Mutation',
          togglePinPost: pinned,
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'Post', id: postId }),
            fields: { pinned: () => pinned },
          });
          cache.modify<{ posts: Reference[] }>({
            id: cache.identify({ __typename: 'Community', id: communityId }),
            fields: {
              posts: (
                existing: readonly Reference[] = [],
                { readField, toReference, storeFieldName },
              ) => {
                if (!storeFieldName.includes(PINNED_STORE_FIELD))
                  return existing;
                if (!pinned) {
                  return existing.filter(
                    (ref) => readField('id', ref) !== postId,
                  );
                }
                const alreadyPinned = existing.some(
                  (ref) => readField('id', ref) === postId,
                );
                if (alreadyPinned) return existing;
                const ref = toReference({ __typename: 'Post', id: postId });
                return ref ? [ref, ...existing] : existing;
              },
            },
          });
        },
      })
        .then(() => {
          toast.success(pinned ? 'Post pinned' : 'Post unpinned');
        })
        .catch(() => {
          toast.error(pinned ? 'Failed to pin post' : 'Failed to unpin post');
        }),
    [communityId, togglePinMutation],
  );
};
